
import { useState, useEffect, useRef } from 'react';
import { Portfolio as PortfolioZod } from '@/schemas/portfolio'; // Use Zod type
// import { useData } from '@/context/DataContext'; // Removed
import { getAuth } from 'firebase/auth';
import { doc, collection, query, where, getDocs, getDoc } from 'firebase/firestore';
import { db } from '@/utils/firebase';

interface DropdownOption {
  value: string;
  label: string;
}

interface UseBusinessDropdownReturn {
  open: boolean;
  setOpen: (open: boolean) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  selectedValue: string;
  setSelectedValue: (value: string) => void;
  portfolioOptions: DropdownOption[];
  filteredOptions: DropdownOption[];
  handleSelectPortfolio: (portfolioId: string, portfolioName: string) => void;
  ownedPortfolios: PortfolioZod[]; // Use Zod type
}

// Improved role normalization for consistent comparison
const normalizeRole = (role: string | undefined): string => {
  if (!role) return '';
  // Convert to lowercase and remove all spaces for consistent comparison
  return role.toLowerCase().replace(/\s+/g, '');
};

// Enhanced filter to check for owner roles with improved role normalization
const filterOwnedPortfolios = async (portfolios: PortfolioZod[]): Promise<PortfolioZod[]> => { // Use Zod type
  // Get current Firebase user
  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid;
  const currentUserEmail = auth.currentUser?.email;
  
  if (!currentUserId && !currentUserEmail) {
    console.warn('No current user found, cannot filter owned portfolios');
    return [];
  }
  
  console.log(`Filtering owned portfolios with user ID: ${currentUserId}, email: ${currentUserEmail}`);
  
  try {
    const ownedPortfolios: PortfolioZod[] = []; // Use Zod type
    
    // For each portfolio, check if the current user is an owner
    for (const portfolio of portfolios) {
      try {
        // First try to check directly from the portfolio object if members array is present
        if (portfolio.members && portfolio.members.length > 0) {
          const isOwner = portfolio.members.some((member: any) => {
            const memberUserId = member.userId;
            const memberEmail = member.email;
            const memberRoleRaw = member.role || '';
            
            // Normalize role for case-insensitive comparison
            const memberRole = normalizeRole(memberRoleRaw);
            
            // Check if this member is the current user (by ID or email)
            const isCurrentUser = 
              (currentUserId && memberUserId === currentUserId) || 
              (currentUserEmail && memberEmail === currentUserEmail);
            
            // Check for any variation of owner roles
            const isOwnerRole = 
              memberRole === 'owner' || 
              memberRole === 'primaryowner' || 
              memberRole === 'primary';
            
            if (isCurrentUser) {
              console.log(`User match found in portfolio ${portfolio.id} - role: ${memberRoleRaw}, normalized: ${memberRole}, isOwner: ${isOwnerRole}`);
            }
            
            return isCurrentUser && isOwnerRole;
          });
          
          if (isOwner) {
            console.log(`Adding portfolio ${portfolio.id} via direct check to owned portfolios list`);
            ownedPortfolios.push(portfolio);
            continue; // Skip Firestore query if we already found it
          } else {
            // If members array is not present or empty on the portfolio object from props,
            // we assume the user is not an owner based on the provided data.
            // No need to re-fetch from Firestore as FirebaseProvider is the source of truth.
            console.log(`Portfolio ${portfolio.id} has no members in provided data or direct check failed. Not re-fetching.`);
          }
        }
        // Removed redundant Firestore query block.
        // The portfolio object passed in should be the source of truth from FirebaseProvider.
      } catch (error) {
        console.error(`Error checking ownership for portfolio ${portfolio.id} (using provided data):`, error);
      }
    }
    
    console.log(`Found ${ownedPortfolios.length} portfolios owned by current user`);
    return ownedPortfolios;
  } catch (error) {
    console.error('Error filtering owned portfolios:', error);
    return [];
  }
};

// Convert portfolios to dropdown options
const createPortfolioOptions = (portfolios: PortfolioZod[]): DropdownOption[] => { // Use Zod type
  return portfolios.map(portfolio => ({
    value: portfolio.id, // Assumes PortfolioZod has id
    label: portfolio.name // Assumes PortfolioZod has name, not title
  }));
};

// Filter options based on search term
const filterOptionsBySearch = (options: DropdownOption[], searchTerm: string): DropdownOption[] => {
  if (!searchTerm.trim()) return options;
  
  return options.filter(option => 
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );
};

export function useBusinessDropdown(
  portfolios: PortfolioZod[], // Use Zod type
  onSelectPortfolio: (portfolioId: string) => void
): UseBusinessDropdownReturn {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const [ownedPortfolios, setOwnedPortfolios] = useState<PortfolioZod[]>([]); // Use Zod type
  const [portfolioOptions, setPortfolioOptions] = useState<DropdownOption[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<DropdownOption[]>([]);
  // const [appData] = useData(); // Removed unused appData
  const auth = getAuth();
  
  // Get owned portfolios
  useEffect(() => {
    const loadOwnedPortfolios = async () => {
      // Log portfolios received
      console.log(`useBusinessDropdown - Received ${portfolios.length} portfolios to filter`);
      
      if (portfolios.length === 0) {
        setOwnedPortfolios([]);
        setPortfolioOptions([]);
        setFilteredOptions([]);
        return;
      }
      
      try {
        // Filter for owned portfolios
        const owned = await filterOwnedPortfolios(portfolios);
        setOwnedPortfolios(owned);
        
        // Create dropdown options
        const options = createPortfolioOptions(owned);
        setPortfolioOptions(options);
        setFilteredOptions(options);
        
        console.log(`useBusinessDropdown - Created ${options.length} portfolio options for dropdown`);
      } catch (error) {
        console.error('Error loading owned portfolios:', error);
        setOwnedPortfolios([]);
        setPortfolioOptions([]);
        setFilteredOptions([]);
      }
    };
    
    loadOwnedPortfolios();
  }, [portfolios]);
  
  // Update filtered options when search value changes
  useEffect(() => {
    setFilteredOptions(filterOptionsBySearch(portfolioOptions, searchValue));
  }, [portfolioOptions, searchValue]);
  
  // Reset search value when dropdown closes
  useEffect(() => {
    if (!open) {
      setSearchValue('');
    }
  }, [open]);
  
  const handleSelectPortfolio = (portfolioId: string, portfolioName: string) => {
    // Capture the selected portfolio ID
    setSelectedValue(portfolioId);
    
    // Call the provided callback passing the portfolio ID
    onSelectPortfolio(portfolioId);
    
    // Close the dropdown
    setOpen(false);
    
    // No need to navigate here or set state that prevents reselection
    console.log(`Selected portfolio: ${portfolioName} (${portfolioId})`);
  };

  return {
    open,
    setOpen,
    searchValue,
    setSearchValue,
    selectedValue,
    setSelectedValue,
    portfolioOptions,
    filteredOptions,
    handleSelectPortfolio,
    ownedPortfolios
  };
}
